import { useRef, useState, useCallback } from 'react';
import { uploadFile } from '../services/api';

/**
 * Shared hook for the photo/video upload trio (input ref + click trigger
 * + upload roundtrip). Designed for the *immediate-upload* pattern where
 * the network request fires when the user picks a file — see
 * `ProfileSettings.handleUploadResume` for the canonical migration.
 *
 * The four other extant call sites (Register, Network, Messages plus
 * Showcase's photo-picker) defer the upload until form submit; migrating
 * those would require a deferred-mode option (or a flow change) and is
 * left for a follow-up. Showcase's slot-based photo importer is a fit
 * but uses `addMedia` after upload — also left for a follow-up.
 *
 * Returns:
 *   - `inputRef`         — attach to a hidden `<input type="file" />`
 *   - `triggerPicker`    — click handler that opens the file dialog
 *   - `onFileChange`     — `<input>`'s onChange handler; uploads + calls onSuccess
 *   - `uploading`        — boolean flag while a request is in flight
 *   - `error`            — last error (or null)
 *   - `reset`            — clear the input value + error state
 *   - `inputProps`       — spread onto the input to wire ref/type/accept/onChange
 *
 * Options:
 *   - `accept`    — file picker accept attribute (default: 'image/*')
 *   - `multiple`  — allow multi-select (default: false)
 *   - `onSuccess(url, file)` — called per uploaded file with the resolved URL
 *   - `onError(err)`         — optional error callback (defaults to console.error)
 *
 * API responses are normalized by the shared axios interceptor, so
 * upload responses expose the payload directly at `res.data`.
 */
export function useFileUpload({
  accept = 'image/*',
  multiple = false,
  validateFile,
  onValidationError,
  onSuccess,
  onError,
} = {}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const triggerPicker = useCallback(() => {
    if (inputRef.current) inputRef.current.click();
  }, []);

  const reset = useCallback(() => {
    if (inputRef.current) inputRef.current.value = '';
    setError(null);
  }, []);

  const onFileChange = useCallback(
    async (event) => {
      const files = Array.from(event.target.files || []);
      if (files.length === 0) return;

      setError(null);

      if (validateFile) {
        for (const file of files) {
          const validationResult = validateFile(file);
          if (validationResult !== true) {
            const validationError =
              validationResult instanceof Error
                ? validationResult
                : new Error(
                    typeof validationResult === 'string' ? validationResult : 'Invalid file'
                  );
            setError(validationError);
            if (onValidationError) onValidationError(validationError, file);
            if (inputRef.current) inputRef.current.value = '';
            if (event.target) event.target.value = '';
            return;
          }
        }
      }

      setUploading(true);
      try {
        for (const file of files) {
          const res = await uploadFile(file);
          const url = res.data?.url;
          if (onSuccess) onSuccess(url, file);
        }
      } catch (err) {
        setError(err);
        if (onError) {
          onError(err);
        } else {
          console.error('useFileUpload: upload failed', err);
        }
      } finally {
        setUploading(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    },
    [validateFile, onValidationError, onSuccess, onError]
  );

  return {
    inputRef,
    triggerPicker,
    onFileChange,
    uploading,
    error,
    reset,
    inputProps: {
      ref: inputRef,
      type: 'file',
      accept,
      multiple,
      onChange: onFileChange,
      style: { display: 'none' },
    },
  };
}

export default useFileUpload;
